// =====================================================
// UserProfileView — design identique à profil.tsx
// SafeImage, LikeButton glow violet, MaterialCommunityIcons,
// ShareButton, MediaViewer, expo-video, stats bar cercle violet
// =====================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  StyleSheet, Text, View, ScrollView,
  TouchableOpacity, ActivityIndicator, Platform, Modal,
  Dimensions, Animated,
} from 'react-native';
import { Ionicons }               from '@expo/vector-icons';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient }         from 'expo-linear-gradient';
import * as Haptics               from 'expo-haptics';
import { VideoView, useVideoPlayer } from 'expo-video';
import { Image }                  from 'react-native';
import { useTranslation }         from 'react-i18next';

import CommentsModal from './CommentsModal';
import LikersModal   from './LikersModal';
import ShareButton   from './ShareButton';

const WS_BASE          = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const USER_PROFILE_URL = `${WS_BASE}/user-profile`;
const HOME_URL         = `${WS_BASE}/home`;
const NATIVE           = Platform.OS !== 'web';

const LIKE_ACTIVE_COLOR   = '#7B1FA2';
const LIKE_INACTIVE_COLOR = '#65676B';

const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');

type FriendStatus = 'none' | 'pending_sent' | 'pending_received' | 'accepted';

interface UserProfile {
  id:             string;
  nom:            string;
  prenom:         string;
  avatar_url?:    string;
  trophies_count: number;
  friends_count:  number;
  created_at:     string;
}

interface Post {
  item_type:    'post';
  id:           string;
  author_id:    string;
  author:       { nom: string; prenom: string; avatar_url: string | null };
  content:      string;
  visibility:   string;
  created_at:   string;
  imagepots?:   string | null;
  vidposts?:    string | null;
  reactions:    { likes: number; comments: number; shares: number };
  user_liked?:  boolean;
  user_shared?: boolean;
  user_saved?:  boolean;
}

interface Props {
  userId:      string;
  viewerId:    string;
  accessToken: string;
  onClose?:    () => void;
  asModal?:    boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const LOCALE_MAP: Record<string, string> = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', pt: 'pt-BR' };

const formatTimeAgo = (dateString: string, t: (key: string, opts?: any) => string) => {
  const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (diff < 60)    return t('userProfileView.time.now');
  if (diff < 3600)  return t('userProfileView.time.minutes', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('userProfileView.time.hours', { count: Math.floor(diff / 3600) });
  return t('userProfileView.time.days', { count: Math.floor(diff / 86400) });
};

const getInitials = (nom: string, prenom: string) =>
  `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();

// ─── SafeImage ────────────────────────────────────────────────────────────────
function SafeImage({ uri, style, resizeMode = 'cover', fallback = null }: {
  uri: string | null | undefined; style: any;
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'center';
  fallback?: React.ReactNode;
}) {
  const [error, setError] = useState(false);
  useEffect(() => { setError(false); }, [uri]);
  if (!uri || error) return fallback ? <>{fallback}</> : null;
  return <Image source={{ uri }} style={style} resizeMode={resizeMode} onError={() => setError(true)} />;
}

// ─── MobileVideoPlayer ────────────────────────────────────────────────────────
function MobileVideoPlayer({ url }: { url: string }) {
  const player = useVideoPlayer({ uri: url }, p => { p.play(); });
  return (
    <VideoView player={player} style={styles.mediaViewerNativeVideo} contentFit="contain" nativeControls />
  );
}

// ─── MediaViewer ─────────────────────────────────────────────────────────────
function MediaViewer({ visible, url, type, onClose }: {
  visible: boolean; url: string | null; type: 'image' | 'video'; onClose: () => void;
}) {
  if (!url) return null;
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <View style={styles.mediaViewerOverlay}>
        <TouchableOpacity style={styles.mediaViewerClose} onPress={onClose}>
          <Ionicons name="close" size={30} color="#fff" />
        </TouchableOpacity>
        {type === 'image' ? (
          <TouchableOpacity activeOpacity={1} onPress={onClose}>
            <SafeImage uri={url} style={styles.mediaViewerImage} resizeMode="contain" />
          </TouchableOpacity>
        ) : Platform.OS === 'web' ? (
          // @ts-ignore
          <video src={url} controls autoPlay style={{ width: '100%', maxHeight: '90%', objectFit: 'contain', backgroundColor: '#000' }} />
        ) : (
          <MobileVideoPlayer url={url} />
        )}
      </View>
    </Modal>
  );
}

// ─── LikeButton — identique à profil.tsx ─────────────────────────────────────
function LikeButton({ liked, isAnim, onPress }: {
  liked: boolean; isAnim: boolean; onPress: () => void;
}) {
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const glowAnim  = useRef(new Animated.Value(0)).current;

  const handlePress = () => {
    if (isAnim) return;
    Animated.parallel([
      Animated.sequence([
        Animated.timing(scaleAnim, { toValue: 1.3, duration: 120, useNativeDriver: true }),
        Animated.spring(scaleAnim, { toValue: 1, friction: 4, tension: 50, useNativeDriver: true }),
      ]),
      Animated.sequence([
        Animated.timing(glowAnim, { toValue: 1, duration: 120, useNativeDriver: false }),
        Animated.timing(glowAnim, { toValue: 0, duration: 400, useNativeDriver: false }),
      ]),
    ]).start();
    onPress();
  };

  const glowColor = glowAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: ['rgba(123,31,162,0)', 'rgba(123,31,162,0.18)'],
  });

  return (
    <TouchableOpacity style={styles.actionBtnModern} onPress={handlePress} disabled={isAnim} activeOpacity={0.7}>
      <Animated.View style={[
        styles.actionContentModern,
        styles.likeBtnInner,
        liked && styles.likeBtnActive,
        { backgroundColor: glowColor as any },
      ]}>
        <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
          <MaterialCommunityIcons
            name={liked ? 'thumb-up' : 'thumb-up-outline'}
            size={26}
            color={liked ? LIKE_ACTIVE_COLOR : LIKE_INACTIVE_COLOR}
          />
        </Animated.View>
      </Animated.View>
    </TouchableOpacity>
  );
}

// ─── PostCard — design identique à profil.tsx ─────────────────────────────────
const PostCard = React.memo(({ post, viewerId, onLike, onShare, onSave, onOpenComments, onOpenLikers, t }: {
  post:           Post;
  viewerId:       string;
  onLike:         (id: string, liked: boolean)  => Promise<void>;
  onShare:        (id: string, shared: boolean) => Promise<void>;
  onSave:         (id: string, saved: boolean)  => Promise<void>;
  onOpenComments: (id: string) => void;
  onOpenLikers:   (id: string) => void;
  t:              (key: string, opts?: any) => string;
}) => {
  const [liked,       setLiked]       = useState(post.user_liked  || false);
  const [shared,      setShared]      = useState(post.user_shared || false);
  const [saved,       setSaved]       = useState(post.user_saved  || false);
  const [likesCount,  setLikesCount]  = useState(post.reactions.likes);
  const [sharesCount, setSharesCount] = useState(post.reactions.shares);
  const [isAnim,      setIsAnim]      = useState(false);
  const [viewerUrl,   setViewerUrl]   = useState<string | null>(null);
  const [viewerType,  setViewerType]  = useState<'image' | 'video'>('image');

  useEffect(() => {
    setLiked(post.user_liked  || false);
    setShared(post.user_shared || false);
    setSaved(post.user_saved  || false);
    setLikesCount(post.reactions.likes);
    setSharesCount(post.reactions.shares);
  }, [post.id, post.user_liked, post.user_shared, post.user_saved, post.reactions]);

  const handleLike = async () => {
    if (isAnim) return; setIsAnim(true);
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    const n = !liked; setLiked(n); setLikesCount(p => n ? p + 1 : p - 1);
    try   { await onLike(post.id, n); }
    catch { setLiked(!n); setLikesCount(p => n ? p - 1 : p + 1); }
    finally { setTimeout(() => setIsAnim(false), 300); }
  };

  const handleShare = async (postId: string, sh: boolean) => {
    setShared(sh); setSharesCount(p => sh ? p + 1 : p - 1);
    try   { await onShare(postId, sh); }
    catch { setShared(!sh); setSharesCount(p => sh ? p - 1 : p + 1); }
  };

  const handleSave = async () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const n = !saved; setSaved(n);
    try   { await onSave(post.id, n); }
    catch { setSaved(!n); }
  };

  return (
    <>
      <MediaViewer visible={!!viewerUrl} url={viewerUrl} type={viewerType} onClose={() => setViewerUrl(null)} />

      <View style={styles.postCard}>

        {/* HEADER */}
        <View style={styles.postHeader}>
          <View style={styles.postAuthor}>
            <SafeImage
              uri={post.author.avatar_url}
              style={styles.postAvatar}
              resizeMode="cover"
              fallback={
                <View style={styles.avatarPlaceholder}>
                  <Text style={styles.avatarText}>{getInitials(post.author.nom, post.author.prenom)}</Text>
                </View>
              }
            />
            <View>
              <Text style={styles.authorName}>{post.author.prenom} {post.author.nom}</Text>
              <Text style={styles.postTime}>{formatTimeAgo(post.created_at, t)}</Text>
            </View>
          </View>
        </View>

        {/* CONTENU */}
        <Text style={styles.postContent}>{post.content}</Text>

        {/* IMAGE — thumbnail statique → MediaViewer */}
        {post.imagepots ? (
          <TouchableOpacity activeOpacity={0.92} onPress={() => { setViewerType('image'); setViewerUrl(post.imagepots!); }}>
            <SafeImage uri={post.imagepots} style={styles.postImage} resizeMode="cover" />
            <View style={styles.mediaExpandHint}>
              <Ionicons name="expand-outline" size={16} color="rgba(255,255,255,0.85)" />
            </View>
          </TouchableOpacity>
        ) : null}

        {/* VIDÉO — thumbnail statique → MediaViewer */}
        {post.vidposts ? (
          <TouchableOpacity
            style={styles.videoThumb}
            activeOpacity={0.92}
            onPress={() => { setViewerType('video'); setViewerUrl(post.vidposts!); }}
          >
            <Ionicons name="play-circle" size={52} color="rgba(255,255,255,0.9)" />
            <Text style={styles.videoLabel}>{t('userProfileView.tapToPlay')}</Text>
          </TouchableOpacity>
        ) : null}

        {/* STATS BAR — cercle violet identique à profil.tsx */}
        <View style={styles.statsBar}>
          <TouchableOpacity onPress={() => { if (likesCount > 0) onOpenLikers(post.id); }}>
            {likesCount > 0 ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <View style={styles.statsLikeCircle}>
                  <MaterialCommunityIcons name="thumb-up" size={10} color="#FFF" />
                </View>
                <Text style={styles.statsText}>{likesCount}</Text>
              </View>
            ) : <Text style={styles.statsText}></Text>}
          </TouchableOpacity>
          <View style={styles.statsRight}>
            {post.reactions.comments > 0 && <Text style={styles.statsText}>{t('userProfileView.commentsCount', { count: post.reactions.comments })}</Text>}
            {post.reactions.comments > 0 && sharesCount > 0 && <Text style={styles.statsSep}>•</Text>}
            {sharesCount > 0 && <Text style={styles.statsText}>{t('userProfileView.sharesCount', { count: sharesCount })}</Text>}
          </View>
        </View>

        {/* ACTIONS BAR — identique à profil.tsx */}
        <View style={styles.actionsBar}>

          {/* LIKE */}
          <LikeButton liked={liked} isAnim={isAnim} onPress={handleLike} />

          {/* COMMENTER */}
          <TouchableOpacity
            style={styles.actionBtnModern}
            onPress={() => {
              if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              onOpenComments(post.id);
            }}
            activeOpacity={0.6}
          >
            <View style={styles.actionContentModern}>
              <Ionicons name="chatbubble-outline" size={22} color="#65676B" />
            </View>
          </TouchableOpacity>

          {/* PARTAGER */}
          {(post.visibility === 'public' || post.visibility === 'pro') && (
            <ShareButton
              postId={post.id}
              content={post.content}
              isPro={post.visibility === 'pro'}
              shared={shared}
              sharesCount={sharesCount}
              onShareHarmonia={handleShare}
            />
          )}

          {/* SAUVEGARDER */}
          <TouchableOpacity
            style={[styles.actionBtnModern, { flex: 0, paddingHorizontal: 12 }]}
            onPress={handleSave}
            activeOpacity={0.6}
          >
            <View style={styles.actionContentModern}>
              <Ionicons name={saved ? 'bookmark' : 'bookmark-outline'} size={22} color={saved ? '#FFD700' : '#65676B'} />
            </View>
          </TouchableOpacity>

        </View>
      </View>
    </>
  );
});

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────
export default function UserProfileView({ userId, viewerId, accessToken, onClose, asModal = false }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'fr';

  const [profile,       setProfile]       = useState<UserProfile | null>(null);
  const [friendStatus,  setFriendStatus]  = useState<FriendStatus>('none');
  const [posts,         setPosts]         = useState<Post[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [loadingPosts,  setLoadingPosts]  = useState(false);
  const [showPosts,     setShowPosts]     = useState(false);
  const [friendLoading, setFriendLoading] = useState(false);

  const [showAvatarViewer,        setShowAvatarViewer]        = useState(false);
  const [showCommentsModal,       setShowCommentsModal]       = useState(false);
  const [selectedPostForComments, setSelectedPostForComments] = useState<string | null>(null);
  const [showLikersModal,         setShowLikersModal]         = useState(false);
  const [selectedPostForLikers,   setSelectedPostForLikers]   = useState<string | null>(null);

  useEffect(() => {
    if (userId && accessToken) loadUserProfile();
  }, [userId, accessToken]);

  const loadUserProfile = async () => {
    setLoading(true);
    try {
      const res  = await fetch(USER_PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-user-profile', access_token: accessToken, target_user_id: userId }),
      });
      const data = await res.json();
      if (res.ok) { setProfile(data.profile); setFriendStatus(data.friendship_status || 'none'); }
    } catch (err) { console.error('[UserProfileView] loadProfile:', err); }
    finally { setLoading(false); }
  };

  const loadUserPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const res  = await fetch(USER_PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-user-posts', access_token: accessToken, target_user_id: userId }),
      });
      const data = await res.json();
      if (res.ok) setPosts(data.posts || []);
    } catch (err) { console.error('[UserProfileView] loadPosts:', err); }
    finally { setLoadingPosts(false); }
  }, [userId, accessToken]);

  const handleFriendAction = async () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setFriendLoading(true);
    try {
      let action = '';
      if (friendStatus === 'none')               action = 'add-friend';
      else if (friendStatus === 'pending_sent')  action = 'remove-friend';
      else if (friendStatus === 'pending_received') action = 'accept-friend';
      else if (friendStatus === 'accepted')      action = 'remove-friend';
      const res  = await fetch(USER_PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, access_token: accessToken, target_user_id: userId }),
      });
      const data = await res.json();
      if (res.ok) {
        setFriendStatus(data.friendship_status);
        if (data.friendship_status === 'accepted') { setShowPosts(true); loadUserPosts(); }
        if (data.friendship_status === 'none') { setShowPosts(false); setPosts([]); }
      }
    } catch (err) { console.error('[UserProfileView] friendAction:', err); }
    finally { setFriendLoading(false); }
  };

  const handleVoirPlus = () => { setShowPosts(true); loadUserPosts(); };

  const handleLike = useCallback(async (postId: string, liked: boolean) => {
    const res  = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: liked ? 'like-post' : 'unlike-post', user_id: viewerId, access_token: accessToken, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, reactions: { ...p.reactions, likes: data.likes }, user_liked: liked } : p
    ));
  }, [viewerId, accessToken]);

  const handleShare = useCallback(async (postId: string, shared: boolean) => {
    const res  = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: shared ? 'share-post' : 'unshare-post', user_id: viewerId, access_token: accessToken, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, reactions: { ...p.reactions, shares: data.shares }, user_shared: shared } : p
    ));
  }, [viewerId, accessToken]);

  const handleSave = useCallback(async (postId: string, saved: boolean) => {
    const res  = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: saved ? 'save-post' : 'unsave-post', user_id: viewerId, access_token: accessToken, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, user_saved: saved } : p
    ));
  }, [viewerId, accessToken]);

  const handleOpenComments = useCallback((postId: string) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setSelectedPostForComments(postId); setShowCommentsModal(true);
  }, []);

  const handleOpenLikers = useCallback((postId: string) => {
    setSelectedPostForLikers(postId); setShowLikersModal(true);
  }, []);

  const renderFriendButton = () => {
    const configs: Record<FriendStatus, { label: string; icon: string; color: string[] }> = {
      none:             { label: t('userProfileView.friend.add'),      icon: 'person-add-outline',   color: ['#7B1FA2', '#4B0082'] },
      pending_sent:     { label: t('userProfileView.friend.pending'),  icon: 'time-outline',          color: ['#999', '#777'] },
      pending_received: { label: t('userProfileView.friend.accept'),  icon: 'checkmark-outline',     color: ['#10B981', '#059669'] },
      accepted:         { label: t('userProfileView.friend.remove'),  icon: 'person-remove-outline', color: ['#EF4444', '#DC2626'] },
    };
    const cfg = configs[friendStatus];
    return (
      <TouchableOpacity onPress={handleFriendAction} disabled={friendLoading} activeOpacity={0.8}>
        <LinearGradient colors={cfg.color as any} style={styles.friendBtn}>
          {friendLoading
            ? <ActivityIndicator size="small" color="#fff" />
            : <><Ionicons name={cfg.icon as any} size={18} color="#fff" /><Text style={styles.friendBtnText}>{cfg.label}</Text></>}
        </LinearGradient>
      </TouchableOpacity>
    );
  };

  const content = (
    <View style={[styles.container, asModal && styles.containerModal]}>

      {/* Bouton fermer modal */}
      {asModal && onClose && (
        <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
          <Ionicons name="close" size={26} color="#fff" />
        </TouchableOpacity>
      )}

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#7B1FA2" />
          <Text style={styles.loadingText}>{t('userProfileView.loading')}</Text>
        </View>
      ) : !profile ? (
        <View style={styles.centered}>
          <Ionicons name="alert-circle-outline" size={64} color="#EF4444" />
          <Text style={styles.errorText}>{t('userProfileView.profileNotFound')}</Text>
          <TouchableOpacity onPress={loadUserProfile} style={styles.retryBtn}>
            <Text style={styles.retryBtnText}>{t('userProfileView.retry')}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>

          {/* ── HEADER GRADIENT ─────────────────────────────────────── */}
          <LinearGradient colors={['#7B1FA2', '#4B0082']} style={styles.header}>
            <TouchableOpacity
              onPress={() => profile.avatar_url && setShowAvatarViewer(true)}
              activeOpacity={profile.avatar_url ? 0.8 : 1}
            >
              <SafeImage
                uri={profile.avatar_url}
                style={styles.avatar}
                resizeMode="cover"
                fallback={
                  <View style={[styles.avatar, styles.avatarFallback]}>
                    <Text style={styles.avatarFallbackText}>
                      {getInitials(profile.nom, profile.prenom)}
                    </Text>
                  </View>
                }
              />
            </TouchableOpacity>
            <Text style={styles.headerName}>{profile.prenom} {profile.nom}</Text>
            <Text style={styles.headerSub}>
              {t('userProfileView.memberSince', {
                date: new Date(profile.created_at).toLocaleDateString(LOCALE_MAP[lang] || 'fr-FR', { month: 'long', year: 'numeric' }),
              })}
            </Text>

            {/* STATS */}
            <View style={styles.statsRowHeader}>
              <LinearGradient colors={['rgba(255,255,255,0.18)', 'rgba(255,255,255,0.10)']} style={styles.statCard}>
                <Ionicons name="people-outline" size={22} color="#fff" />
                <Text style={styles.statCardValue}>{profile.friends_count || 0}</Text>
                <Text style={styles.statCardLabel}>{t('userProfileView.friendsLabel')}</Text>
              </LinearGradient>
              <LinearGradient colors={['rgba(255,215,0,0.25)', 'rgba(255,140,0,0.20)']} style={styles.statCard}>
                <Ionicons name="trophy-outline" size={22} color="#FFD700" />
                <Text style={styles.statCardValue}>{profile.trophies_count}</Text>
                <Text style={styles.statCardLabel}>{t('userProfileView.trophiesLabel')}</Text>
              </LinearGradient>
            </View>

            {/* BOUTON AMI */}
            <View style={styles.friendBtnContainer}>{renderFriendButton()}</View>
          </LinearGradient>

          {/* ── PUBLICATIONS ─────────────────────────────────────────── */}
          <View style={styles.postsSection}>

            {friendStatus === 'accepted' && !showPosts && (
              <TouchableOpacity style={styles.voirPlusBtn} onPress={handleVoirPlus} activeOpacity={0.8}>
                <LinearGradient colors={['#7B1FA2', '#4B0082']} style={styles.voirPlusGrad}>
                  <Ionicons name="eye-outline" size={18} color="#fff" />
                  <Text style={styles.voirPlusText}>{t('userProfileView.viewPosts')}</Text>
                </LinearGradient>
              </TouchableOpacity>
            )}

            {showPosts && (
              <>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>{t('userProfileView.postsCount', { count: posts.length })}</Text>
                </View>
                {loadingPosts ? (
                  <View style={styles.postsLoading}>
                    <ActivityIndicator size="small" color="#7B1FA2" />
                  </View>
                ) : posts.length === 0 ? (
                  <View style={styles.emptyPosts}>
                    <Ionicons name="newspaper-outline" size={48} color="#DDD" />
                    <Text style={styles.emptyPostsText}>{t('userProfileView.noPosts')}</Text>
                  </View>
                ) : (
                  posts.map(post => (
                    <PostCard
                      key={post.id} post={post} viewerId={viewerId}
                      onLike={handleLike} onShare={handleShare} onSave={handleSave}
                      onOpenComments={handleOpenComments} onOpenLikers={handleOpenLikers}
                      t={t}
                    />
                  ))
                )}
              </>
            )}

            {friendStatus !== 'accepted' && (
              <View style={styles.notFriendBox}>
                <Ionicons
                  name={friendStatus === 'pending_sent' ? 'time-outline' : 'lock-closed-outline'}
                  size={40}
                  color="#DDD"
                />
                <Text style={styles.notFriendText}>
                  {friendStatus === 'pending_sent'
                    ? t('userProfileView.notFriend.pendingSent')
                    : friendStatus === 'pending_received'
                    ? t('userProfileView.notFriend.pendingReceived')
                    : t('userProfileView.notFriend.none')}
                </Text>
              </View>
            )}

          </View>
        </ScrollView>
      )}

      {/* ── VIEWER AVATAR ──────────────────────────────────────────── */}
      <Modal
        visible={showAvatarViewer}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAvatarViewer(false)}
        statusBarTranslucent
      >
        <View style={styles.mediaViewerOverlay}>
          <TouchableOpacity style={styles.mediaViewerClose} onPress={() => setShowAvatarViewer(false)}>
            <Ionicons name="close" size={30} color="#fff" />
          </TouchableOpacity>
          {profile?.avatar_url && (
            <SafeImage uri={profile.avatar_url} style={styles.mediaViewerImage} resizeMode="contain" />
          )}
        </View>
      </Modal>

      <CommentsModal
        visible={showCommentsModal} postId={selectedPostForComments} userId={viewerId}
        onClose={() => { setShowCommentsModal(false); setSelectedPostForComments(null); }}
        onCommentAdded={() => loadUserPosts()}
      />
      <LikersModal
        visible={showLikersModal} postId={selectedPostForLikers}
        onClose={() => { setShowLikersModal(false); setSelectedPostForLikers(null); }}
      />
    </View>
  );

  if (asModal) {
    return <Modal visible animationType="slide" onRequestClose={onClose}>{content}</Modal>;
  }
  return content;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:      { flex: 1, backgroundColor: '#F5F5F5' },
  containerModal: { flex: 1 },
  scrollContent:  { paddingBottom: 80 },
  centered:       { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40, minHeight: 300 },
  loadingText:    { marginTop: 16, fontSize: 16, color: '#666' },
  errorText:      { marginTop: 16, fontSize: 16, color: '#EF4444', textAlign: 'center' },
  retryBtn:       { marginTop: 20, paddingHorizontal: 24, paddingVertical: 12, backgroundColor: '#7B1FA2', borderRadius: 10 },
  retryBtnText:   { color: '#fff', fontSize: 16, fontWeight: '600' },
  closeBtn:       { position: 'absolute', top: Platform.OS === 'ios' ? 54 : 30, right: 16, zIndex: 100, backgroundColor: 'rgba(123,31,162,0.8)', borderRadius: 20, padding: 8 },

  // Header
  header:      { paddingTop: Platform.OS === 'ios' ? 64 : 44, paddingBottom: 28, alignItems: 'center', paddingHorizontal: 20 },
  avatar:      { width: 110, height: 110, borderRadius: 55, borderWidth: 4, borderColor: '#fff', backgroundColor: '#E0E0E0', marginBottom: 14 },
  avatarFallback:     { justifyContent: 'center', alignItems: 'center', backgroundColor: '#6A0DAD' },
  avatarFallbackText: { color: '#fff', fontSize: 36, fontWeight: 'bold' },
  headerName:  { fontSize: 24, fontWeight: 'bold', color: '#fff', marginBottom: 4 },
  headerSub:   { fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 18 },

  // Stats header cards
  statsRowHeader: { flexDirection: 'row', gap: 12, width: '100%', marginBottom: 18 },
  statCard:       { flex: 1, borderRadius: 14, padding: 14, alignItems: 'center', gap: 3 },
  statCardValue:  { fontSize: 20, fontWeight: 'bold', color: '#fff', marginTop: 2 },
  statCardLabel:  { fontSize: 12, color: 'rgba(255,255,255,0.85)' },

  friendBtnContainer: { width: '100%', alignItems: 'center' },
  friendBtn:          { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 28, paddingVertical: 13, borderRadius: 25, minWidth: 200, justifyContent: 'center' },
  friendBtnText:      { color: '#fff', fontSize: 15, fontWeight: '700' },

  // Publications
  postsSection:  { paddingHorizontal: 12, marginTop: 20 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingHorizontal: 4 },
  sectionTitle:  { fontSize: 17, fontWeight: 'bold', color: '#2D0072' },
  postsLoading:  { paddingVertical: 30, alignItems: 'center' },
  emptyPosts:    { alignItems: 'center', paddingVertical: 40, gap: 8 },
  emptyPostsText:{ fontSize: 15, color: '#999', fontWeight: '600' },

  voirPlusBtn:  { marginHorizontal: 4, marginBottom: 16 },
  voirPlusGrad: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 16, borderRadius: 14 },
  voirPlusText: { color: '#fff', fontSize: 16, fontWeight: '700' },

  notFriendBox:  { alignItems: 'center', paddingVertical: 50, gap: 14, paddingHorizontal: 24 },
  notFriendText: { fontSize: 14, color: '#999', textAlign: 'center', lineHeight: 22 },

  // PostCard — identique à profil.tsx
  postCard:          { backgroundColor: '#fff', marginVertical: 6, borderRadius: 14, paddingVertical: 14 },
  postHeader:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, marginBottom: 10 },
  postAuthor:        { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 10 },
  postAvatar:        { width: 40, height: 40, borderRadius: 20 },
  avatarPlaceholder: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  avatarText:        { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  authorName:        { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  postTime:          { fontSize: 12, color: '#999', marginTop: 1 },
  postContent:       { fontSize: 14, color: '#333', lineHeight: 20, paddingHorizontal: 14, marginBottom: 10 },
  postImage:         { width: '100%', height: 260, marginBottom: 10 },
  videoThumb:        { width: '100%', height: 180, backgroundColor: '#1A1A2E', justifyContent: 'center', alignItems: 'center', marginBottom: 10, gap: 8 },
  videoLabel:        { color: 'rgba(255,255,255,0.75)', fontSize: 13 },
  mediaExpandHint:   { position: 'absolute', bottom: 18, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: 4 },

  // Stats bar — cercle violet identique à profil.tsx
  statsBar:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10 },
  statsLikeCircle: { backgroundColor: '#7B1FA2', width: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center' },
  statsText:       { fontSize: 13, color: '#65676B' },
  statsRight:      { flexDirection: 'row', alignItems: 'center', gap: 5 },
  statsSep:        { color: '#CCC' },

  // Actions bar — identique à profil.tsx
  actionsBar:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly', borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#CED0D4', paddingTop: 4, paddingBottom: 4, marginHorizontal: 12, marginTop: 4 },
  actionBtnModern:     { flex: 1, paddingVertical: 8, borderRadius: 8 },
  actionContentModern: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  likeBtnInner:        { borderRadius: 8, paddingVertical: 4, paddingHorizontal: 8 },
  likeBtnActive:       { shadowColor: '#7B1FA2', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.35, shadowRadius: 8, elevation: 4 },

  // MediaViewer
  mediaViewerOverlay:     { flex: 1, backgroundColor: 'rgba(0,0,0,0.97)', justifyContent: 'center', alignItems: 'center' },
  mediaViewerClose:       { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, right: 20, zIndex: 10, padding: 8, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20 },
  mediaViewerImage:       { width: SCREEN_W, height: SCREEN_H * 0.85 },
  mediaViewerNativeVideo: { width: SCREEN_W, height: SCREEN_H * 0.75 },
});
